import { useState, type FormEvent } from 'react';
import {
  createBelonging,
  KEEPALIVE,
  lendBelonging,
  listBelongings,
  listPeople,
  returnBelonging,
  updateBelonging,
} from '../lifeos/api.js';
import { daysBetween, money, shortDate, since, ymdInHomeZone } from '../lifeos/format.js';
import FormSheet from '../lifeos/FormSheet.js';
import { cents, text, toValues } from '../lifeos/forms.js';
import Sheet from '../lifeos/Sheet.js';
import { sphereColor, useSpheres } from '../lifeos/spheres.js';
import { useToast } from '../lifeos/toast.js';
import { useLoad } from '../lifeos/useLoad.js';
import { Button, EmptyState, Group, Icon, IconButton, Loader, Page, Row, Meta, useParamSetter, useSphereLens, When } from '../ui/index.js';
import type { Belonging } from '../lifeos/types.js';

/**
 * Belongings (`/belongings`): things worth remembering — warranties, where
 * something is, who borrowed it. Lend and Return are the row actions, each
 * with a 5-second Undo.
 */
export default function Belongings(): JSX.Element {
  const [params, setParam] = useParamSetter();
  const sphere = params.get('sphere');
  const spheres = useSpheres();
  const lens = useSphereLens(spheres, sphere, (k) => setParam('sphere', k));
  const list = useLoad(() => listBelongings({ sphere: sphere ?? undefined }), [sphere]);
  // Borrowers who are contacts are named from People (the row stores the id only).
  const lentToContact = list.data?.some((b) => b.lentToContactId !== null && !b.lentToText) ?? false;
  const people = useLoad(() => (lentToContact ? listPeople() : Promise.resolve([])), [lentToContact]);
  const toast = useToast();
  const [editing, setEditing] = useState<Belonging | 'new' | null>(null);
  const [lending, setLending] = useState<Belonging | null>(null);
  const [menu, setMenu] = useState<Belonging | null>(null);
  // Optimistic lend state: id → borrower name, or null for "returned".
  const [pending, setPending] = useState<Record<string, string | null>>({});
  const today = ymdInHomeZone(new Date());
  const now = new Date();

  function stage(b: Belonging, to: string | null, text: string, commit: () => Promise<unknown>): void {
    const clear = (): void =>
      setPending((p) => {
        const n = { ...p };
        delete n[b.id];
        return n;
      });
    setPending((p) => ({ ...p, [b.id]: to }));
    toast.defer({
      text,
      commit,
      onUndo: clear,
      onDone: () => void list.reload().finally(clear),
      onError: clear,
      failText: `Couldn't update ${b.name}`,
    });
  }

  const lentToOf = (b: Belonging): string | null | undefined =>
    b.id in pending
      ? pending[b.id]
      : (b.lentToText ?? (b.lentToContactId !== null ? (people.data?.find((p) => p.id === b.lentToContactId)?.name ?? 'someone') : null));
  const lentCount = list.data?.filter((b) => lentToOf(b)).length ?? 0;

  return (
    <Page
      title="Belongings"
      sub={lentCount ? `${lentCount} lent out` : undefined}
      actions={
        <>
          {lens.button}
          <IconButton icon="plus" label="Add" onClick={() => setEditing('new')} />
        </>
      }
    >
      {lens.chip && <div className="ui-chips">{lens.chip}</div>}
      <Loader state={list}>
        {(items) =>
          items.length === 0 ? (
            <EmptyState
              icon="package"
              title="Nothing tracked yet."
              hint="Add things with a warranty, or that people borrow."
              action={
                <Button variant="secondary" icon="plus" onClick={() => setEditing('new')}>
                  Add one
                </Button>
              }
            />
          ) : (
            <Group label="Belongings">
              {items.map((b) => {
                const lentTo = lentToOf(b);
                const warranty = b.warrantyUntil ? daysBetween(today, b.warrantyUntil) : null;
                const returned = (): void => stage(b, null, `Returned ${b.name}`, () => returnBelonging(b.id, KEEPALIVE));
                return (
                  <Row
                    key={b.id}
                    onClick={() => setEditing(b)}
                    ariaLabel={`Edit ${b.name}`}
                    onLongPress={() => setMenu(b)}
                    title={b.name}
                    sub={
                      <>
                        {lentTo && (
                          <span className="la-line la-line--accent">
                            Lent to {lentTo}
                            {b.lentOn && !(b.id in pending) && ` · ${since(`${b.lentOn}T12:00:00Z`, now)}`}
                          </span>
                        )}
                        {warranty !== null && b.warrantyUntil && (
                          <When tone={warranty < 0 ? 'past' : warranty <= 30 ? 'soon' : 'later'}>
                            {warranty < 0 ? 'Warranty over' : `Warranty to ${shortDate(b.warrantyUntil, today)}`}
                          </When>
                        )}
                        <Meta color={sphereColor(spheres, b.sphereKey)}>
                          {[b.category, b.location, b.valueCents !== null ? money(b.valueCents) : null].filter(Boolean).join(' · ') || 'No details'}
                        </Meta>
                      </>
                    }
                    chevron={false}
                    trailing={
                      lentTo ? (
                        <Button size="sm" variant="secondary" onClick={returned}>
                          Returned
                        </Button>
                      ) : (
                        <button type="button" className="la-menu" aria-label={`More for ${b.name}`} onClick={() => setMenu(b)}>
                          <Icon name="more" size={20} />
                        </button>
                      )
                    }
                    swipe={
                      lentTo
                        ? { leading: { label: 'Returned', icon: 'check', tone: 'good', onAction: returned } }
                        : { trailing: [{ label: 'Lend', icon: 'users', tone: 'accent', onAction: () => setLending(b) }] }
                    }
                  />
                );
              })}
            </Group>
          )
        }
      </Loader>
      {lens.sheet}
      {menu && (
        <Sheet title={menu.name} onClose={() => setMenu(null)}>
          <div className="lo-sheet__list lo-sheet__list--first">
            <button
              type="button"
              className="lo-sheet__row"
              onClick={() => {
                const b = menu;
                setMenu(null);
                setLending(b);
              }}
            >
              Lend
              <span className="lo-sheet__rowhint">remember who has it</span>
            </button>
            <button
              type="button"
              className="lo-sheet__row"
              onClick={() => {
                const b = menu;
                setMenu(null);
                setEditing(b);
              }}
            >
              Edit
            </button>
          </div>
        </Sheet>
      )}

      {lending && (
        <LendSheet
          item={lending}
          onClose={() => setLending(null)}
          onLend={(to, name) => {
            const b = lending;
            setLending(null);
            stage(b, name, `Lent ${b.name} to ${name}`, () => lendBelonging(b.id, to, KEEPALIVE));
          }}
        />
      )}
      {editing && (
        <FormSheet
          title={editing === 'new' ? 'Add a belonging' : `Edit ${editing.name}`}
          submitLabel={editing === 'new' ? 'Add' : 'Save'}
          fields={[
            { key: 'name', label: 'Name', required: true, wide: true },
            { key: 'category', label: 'Category', placeholder: 'e.g. tools' },
            { key: 'location', label: 'Where it is' },
            { key: 'valueCents', label: 'Value ($)', kind: 'money' },
            { key: 'purchasedOn', label: 'Bought', kind: 'date' },
            { key: 'purchasedFrom', label: 'Bought from' },
            { key: 'warrantyUntil', label: 'Warranty until', kind: 'date' },
            { key: 'sphereKey', label: 'Sphere', kind: 'select', options: spheres.map((s) => ({ value: s.key, label: s.name })) },
            { key: 'notes', label: 'Notes', kind: 'textarea' },
          ]}
          initial={editing === 'new' ? { sphereKey: 'personal' } : toValues({ ...editing }, ['valueCents'])}
          onClose={() => setEditing(null)}
          onSubmit={async (v) => {
            const body = {
              name: text(v.name)!,
              category: text(v.category),
              location: text(v.location),
              valueCents: cents(v.valueCents, 'Value'),
              purchasedOn: text(v.purchasedOn),
              purchasedFrom: text(v.purchasedFrom),
              warrantyUntil: text(v.warrantyUntil),
              sphereKey: v.sphereKey || undefined,
              notes: text(v.notes),
            };
            if (editing === 'new') await createBelonging(body);
            else await updateBelonging(editing.id, body);
            void list.reload();
          }}
        />
      )}
    </Page>
  );
}

/** Who borrowed it: a person from People when the name matches exactly, else free text. */
function LendSheet({
  item,
  onClose,
  onLend,
}: {
  item: Belonging;
  onClose: () => void;
  onLend: (to: { contactId: number } | { text: string }, name: string) => void;
}): JSX.Element {
  const people = useLoad(() => listPeople(), []);
  const [name, setName] = useState('');

  function submit(e: FormEvent): void {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    const match = people.data?.find((p) => p.name.toLowerCase() === n.toLowerCase());
    onLend(match ? { contactId: match.id } : { text: n }, match?.name ?? n);
  }

  return (
    <Sheet title={`Lend ${item.name}`} onClose={onClose}>
      <form className="la-lend" onSubmit={submit}>
        <input
          className="la-field"
          list="lend-people"
          placeholder="To whom?"
          aria-label="To whom?"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <datalist id="lend-people">
          {(people.data ?? []).map((p) => (
            <option key={p.id} value={p.name} />
          ))}
        </datalist>
        <Button type="submit" size="lg" block disabled={!name.trim()}>
          Lend
        </Button>
      </form>
    </Sheet>
  );
}
